import { HudPiece } from "game-common/models"
import { Eased } from "./chapter_reward_motion"

/**
 * How much of the camp HUD is on screen, piece by piece: the one object GuiManager reads, every
 * frame, to decide it. Signals change it; the GUI only draws what it says. It knows nothing about
 * camps or chapters.
 *
 *   - HIDDEN pieces are whatever the server last said to hold back (a "hud" screen message, decided
 *     by the story chapter: chapter_hud.ts). Anything may set them through `setHidden`. Hiding SNAPS
 *     (it lands as a scene or chapter is taken up, under its title card); showing eases in.
 *   - BARE: the server can ask for nothing but the world (chapter 0, the wreck beach). Every piece is
 *     held back, and GuiManager hides its other ambient widgets and keeps the pack screen shut.
 *   - FOCUS, from a playtest of chapter one: "start hiding UI temporarily when you expect the player
 *     to be reading some dedicated popup". While a held line is up (a bubble with a Next button,
 *     waiting for the player to read it) the HUD around the edges goes away, so the bubble is the one
 *     thing on screen to read. It stays away through the gap between one held line and the next, and
 *     from a title card to the line that opens the chapter, so a conversation never makes it blink.
 *     The player's own pieces never go: their hearts and ready bar (a line can be held while
 *     something hits them) and the checklist, which is what they are doing. Some held lines stay up
 *     while the player acts on them (the Lord's "put the axe through it" waits out the whole chop),
 *     and a checklist that went with them blinked off and on between one and the next.
 */

export interface HudMoment {
    /** A held line's Next button is up where the player can read it. */
    held: boolean
    /** A title card is up. */
    announcing?: boolean
    /** A tutorial clue is pointing at a control. */
    clue: boolean
}

/**
 * Longer than the gap between two held lines: a press, the speaker's next tick, the wire. Measured at
 * ~1.7s between the Lord's lines, and 1.2s let the HUD flash up in every gap.
 */
export const FOCUS_LINGER_MS = 2500
/**
 * How long the HUD stays away after a title card goes, waiting for the line that opens the chapter.
 * The Lord's greeting lands ~2.5s after Begin; without this the HUD came up as the card lifted and
 * went again under his first word.
 */
export const OPENING_HOLD_MS = 5000
export const PRESENCE_MS = 400

const PIECES: HudPiece[] = ["chips", "minimap", "roster", "checklist", "fireGauge", "vitals", "readyBar"]
// The camp's, not the player's: these go while a line is read.
const STEPS_BACK: HudPiece[] = ["chips", "minimap", "roster", "fireGauge"]

export class HudPresence {
    private clock = 0
    private heldUntil = -Infinity
    private hidden = new Set<HudPiece>()
    private bare = false
    private shown = new Map<HudPiece, Eased>()
    private focus = new Map<HudPiece, Eased>()
    // CONVERSING: the player is talking with someone (gui/conversation_panel.ts). Every piece goes, the player's own
    // too, and GuiManager takes every other ambient widget with them, as for a bare screen.
    private conversing = false
    private conversation = new Eased(1, PRESENCE_MS)

    constructor() {
        PIECES.forEach(piece => {
            this.shown.set(piece, new Eased(1, PRESENCE_MS))
            this.focus.set(piece, new Eased(1, PRESENCE_MS))
        })
    }

    /** Hold back exactly these pieces from now on (all of them, if bare); the rest come (back) in. */
    setHidden = (pieces: HudPiece[], bare = false) => {
        this.bare = bare
        this.hidden = new Set(bare ? PIECES : pieces)
        PIECES.forEach(piece => {
            if (this.hidden.has(piece)) this.shown.get(piece)!.snap(0)
            else this.shown.get(piece)!.set(1, this.clock)
        })
    }

    /** A conversation is (or is no longer) on screen. */
    setConversing = (on: boolean) => {
        if (on === this.conversing) return
        this.conversing = on
        this.conversation.set(on ? 0 : 1, this.clock)
    }

    update = (deltaMs: number, moment: HudMoment) => {
        this.clock += Math.max(0, deltaMs)
        if (moment.held) this.heldUntil = Math.max(this.heldUntil, this.clock + FOCUS_LINGER_MS)
        if (moment.announcing) this.heldUntil = Math.max(this.heldUntil, this.clock + OPENING_HOLD_MS)
        const focused = this.focused()
        STEPS_BACK.forEach(piece => {
            const exempt = piece === "chips" && moment.clue
            this.focus.get(piece)!.set(focused && !exempt ? 0 : 1, this.clock)
        })
    }

    /** What to write to the piece's alpha this frame. */
    alpha = (piece: HudPiece): number =>
        this.shown.get(piece)!.value(this.clock) * this.focus.get(piece)!.value(this.clock) * this.conversation.value(this.clock)

    /** Whether the piece is held back altogether (and out of its layout). */
    hides = (piece: HudPiece): boolean => this.hidden.has(piece)

    /** Anything the player cannot see should not catch a click: pixi hit-tests a transparent object. */
    takesInput = (piece: HudPiece): boolean => !this.hides(piece) && this.alpha(piece) > 0.05

    /** How far the piece has arrived, from 0 to 1, leaving out focus: for layout that makes room for it. */
    arrival = (piece: HudPiece): number => this.shown.get(piece)!.value(this.clock)

    focused = (): boolean => this.clock < this.heldUntil

    /** Nothing but the world on screen. */
    isBare = (): boolean => this.bare

    /** A conversation holds the screen: nothing else of the HUD is on it. */
    isConversing = (): boolean => this.conversing
}
